// src/components/Departments/DepartmentsTable.jsx
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material";

import { useGetDepartments } from "../../hooks/Departments/useGetDepartments";
import { useDeleteDepartment } from "../../hooks/Departments/useDeleteDepartment";
import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import DepartmentsTableRow from "./Table/DepartmentsTableRow";
import DepartmentsTableColumns from "./Table/DepartmentsTableColumns";
import { usePermissions } from "../../auth/PermissionsContext";

const DepartmentsTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";
    const { has } = usePermissions();
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    const navigate = useNavigate();

    const perms = useMemo(() => ({
        list: has("departments_list"),
        create: has("departments_create"),
        edit: has("departments_edit"),
        delete: has("departments_delete"),
    }), [has]);

    const { deleteDepartment, isDeleting } = useDeleteDepartment();

    const {
        page,
        perPage,
        search,
        order,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handlePageChange,
        handlePerPageChange,
    } = useTableActions({
        basePath: "/departments",
        deleteSingle: deleteDepartment,
    });

    const filters = useMemo(() => ({
        search: search || undefined,
        page,
        per_page: perPage,
    }), [search, page, perPage]);

    const { isLoading, error, departments, meta } = useGetDepartments(filters);

    const renderRow = (params) => (
        <DepartmentsTableRow
            key={params.item.id}
            department={params.item}
            index={params.index}
            isDark={isDark}
            selectedIds={selectedIds}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
            menuDisabled={!perms.edit && !perms.delete}
        />
    );

    return (
        <TableLayout
            title={isArabic ? "الأقسام" : "Departments"}
            subtitle={isArabic ? "إدارة أقسام المنتجات في النظام" : "Manage product departments in the system"}
            search={search}
            onSearchChange={setSearch}
            addButtonLabel={isArabic ? "إضافة قسم" : "Add Department"}
            onAddClick={() => navigate("/departments/create")}
            addButtonVisible={perms.create}
            isLoading={isLoading}
            error={error}
            items={departments}
            meta={meta}
            page={page}
            perPage={perPage}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            order={order}
            onRequestSort={handleRequestSort}
            columns={DepartmentsTableColumns}
            renderRow={renderRow}
            menuAnchorEl={menuAnchorEl}
            openMenu={Boolean(menuAnchorEl)}
            onMenuOpen={handleMenuOpen}
            onMenuClose={handleMenuClose}
            onMenuDelete={handleMenuDelete}
            onMenuEdit={handleEdit}
            menuDisableEdit={!perms.edit}
            menuDisableDelete={!perms.delete}
            isDeletingSingle={isDeleting}
            selectedIds={selectedIds}
            onSelectAllClick={(e) => handleSelectAllClick(e, departments)}
            onRowCheckboxClick={handleRowCheckboxClick}
            emptyLabelAr="لا توجد أقسام"
            emptyLabelEn="No departments found"
        />
    );
};

export default DepartmentsTable;
